import { Box, Card, CardContent, Chip, Divider, Input, Stack, Tab, TabList, Tabs, Tooltip, Typography } from '@mui/joy';
import { useVirtualizer } from '@tanstack/react-virtual';
import { addDays, isAfter } from 'date-fns';
import React, { FC, useDeferredValue } from 'react';
import { AiOutlineUserAdd } from 'react-icons/ai';
import { HiOutlineSearch } from 'react-icons/hi';
import { TbQrcode } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { NewPatientQRDialog, PatientAddDialog } from '@MP/features/patient';
import { ModuleUtils, useScanModuleStore } from '@MP/features/scan';
import { useFetchOrganizationOfCurrentUser } from '@MP/shared/api/hooks/auth';
import { Handedness, Patient } from '@MP/shared/api/hooks/patient';
import { RectangleButton } from '@MP/shared/components/Button';
import { selectIsTempUser, selectUser, selectUsers, usePatientStore } from '@MP/shared/providers/PatientProvider';
import { TEMP_USR_ID_PREFIX } from '@MP/shared/stores/createPatientStore';
import GenderUtils from '@MP/shared/utils/GenderUtils';
import HandednessUtils from '@MP/shared/utils/HandednessUtils';
import PhoneNumberUtils from '@MP/shared/utils/PhoneNumberUtils';

const PatientListContents = () => {
  const [filter, setFilter] = React.useState('');
  const [openAdd, setOpenAdd] = React.useState(false);
  const [openQR, setOpenQR] = React.useState(false);
  const selectedUser = usePatientStore(selectUser);
  const users = usePatientStore(useShallow(selectUsers));
  const isTempUser = usePatientStore(selectIsTempUser);
  const setUser = usePatientStore((s) => s.setUser);
  const navigate = useNavigate();
  const getModuleByUser = useScanModuleStore((s) => s.getModuleByUser);
  const { data: org } = useFetchOrganizationOfCurrentUser();

  // 선택 환자의 미동의 여부(isTempUser)가 바뀌면 해당 탭으로 전환하되, 그 사이 수동 탭 전환은 유지.
  // effect 대신 렌더 중 이전값 비교로 조정한다.
  const [tabIdx, setTabIdx] = React.useState(isTempUser ? 1 : 0);
  const [prevIsTempUser, setPrevIsTempUser] = React.useState(isTempUser);
  if (isTempUser !== prevIsTempUser) {
    setPrevIsTempUser(isTempUser);
    setTabIdx(isTempUser ? 1 : 0);
  }

  const parentRef = React.useRef(null);

  const deferredFilter = useDeferredValue(filter);

  const filterText = deferredFilter.toLowerCase();
  const filteredUsers = users
    .filter((user) => {
      const isTempUser = user.id.startsWith(TEMP_USR_ID_PREFIX);
      return tabIdx === 0 ? !isTempUser : isTempUser;
    })
    .filter((user) => {
      return (
        user.name.toLowerCase().includes(filterText) ||
        user.phoneNumber?.includes(filterText) ||
        user.code?.toLowerCase().includes(filterText)
      );
    });

  const handleChange = (user: Patient) => {
    setUser(user);
    // 다른 환자로 바꾸면 이전 환자에서 보던 탭이 아니라 대시보드부터 보여준다.
    if (user.id !== selectedUser?.id) {
      navigate('/patient/summary');
    }
  };

  const rowVirtualizer = useVirtualizer({
    count: filteredUsers.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120,
  });

  return (
    <Stack sx={{ px: '16px', py: '20px', width: 320, maxWidth: 320, bgcolor: 'common.white', height: '100%' }}>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#333' }}>환자 관리</Typography>
      </Stack>
      <Stack direction={'row'} spacing={'16px'} my={'12px'}>
        <RectangleButton
          variant="outlined"
          startDecorator={<TbQrcode />}
          color="neutral"
          sx={{ height: 40, minWidth: 130 }}
          onClick={() => setOpenQR(true)}>
          <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>QR 코드</Typography>
        </RectangleButton>
        <RectangleButton
          variant="outlined"
          startDecorator={<AiOutlineUserAdd />}
          color="neutral"
          sx={{ height: 40, minWidth: 130 }}
          onClick={() => setOpenAdd(true)}>
          <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>환자 추가</Typography>
        </RectangleButton>
      </Stack>
      <Divider sx={{ mb: '12px' }} />
      <Box sx={{ mb: '20px' }}>
        <Input
          value={filter}
          startDecorator={<HiOutlineSearch />}
          placeholder="이름, 전화번호, 코드로 검색"
          sx={{ height: '48px' }}
          onChange={(e) => setFilter(e.target.value)}
        />
      </Box>
      <Box sx={{ mb: '16px' }}>
        <Tabs value={tabIdx} onChange={(_, idx) => setTabIdx(idx as number)}>
          <TabList tabFlex="auto">
            <Tab>
              <Typography level="body-sm">동의 환자</Typography>
            </Tab>
            <Tab>
              <Typography level="body-sm">미동의 환자</Typography>
            </Tab>
          </TabList>
        </Tabs>
      </Box>
      <Box ref={parentRef} sx={{ flexGrow: 1, mx: '-16px', px: '16px', overflow: 'auto', minWidth: 320 }}>
        <Box sx={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map((virtualItem) => {
            const user = filteredUsers[virtualItem.index];
            const module = getModuleByUser(user.id);
            return (
              <Box
                key={user.id}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    bgcolor: 'common.white',
                    borderColor: user.id === selectedUser?.id ? 'primary.500' : undefined,
                    mb: '12px',
                  }}>
                  <CardContent sx={{ rowGap: 0 }} onClick={() => handleChange(user)}>
                    <Stack direction={'row'} justifyContent={'space-between'} sx={{ mb: '8px' }} spacing={'16px'}>
                      <Tooltip title={`${user.code}`}>
                        <Typography sx={{ fontSize: '13px', color: '#848484' }} noWrap>
                          [{user.code}]
                        </Typography>
                      </Tooltip>

                      {module && (
                        <Typography sx={{ fontSize: '13px', color: 'red' }}>
                          {`${ModuleUtils.toSensorText(module)} 사용 중`}
                        </Typography>
                      )}

                      <TempUserStatus user={user} />
                    </Stack>
                    <Box>
                      <Tooltip title={`${user.name}`}>
                        <Typography sx={{ fontSize: '18px', fontWeight: '600' }} noWrap>
                          {user.name}
                        </Typography>
                      </Tooltip>
                      <Stack
                        direction={'row'}
                        alignItems={'center'}
                        divider={<Divider orientation="vertical" sx={{ height: '10px', alignSelf: 'center' }} />}
                        spacing={'4px'}>
                        <Typography sx={{ fontSize: '13px' }}>{user.birthYear}년생</Typography>
                        {user.gender && (
                          <Typography sx={{ fontSize: '13px' }}>{GenderUtils.displayName(user.gender)}</Typography>
                        )}
                        {user.phoneNumber && (
                          <Typography sx={{ fontSize: '13px' }}>
                            {PhoneNumberUtils.appendDash(user.phoneNumber)}
                          </Typography>
                        )}
                        {user.handedness !== Handedness.None && (
                          <Typography sx={{ fontSize: '13px' }}>
                            {HandednessUtils.displayName(user.handedness)}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>
      {org?.id && <PatientAddDialog open={openAdd} orgId={org.id} onClose={() => setOpenAdd(false)} />}
      <NewPatientQRDialog open={openQR} onClose={() => setOpenQR(false)} />
    </Stack>
  );
};

const TempUserStatus: FC<{ user: Patient }> = ({ user }) => {
  const isExpired = isAfter(new Date(), addDays(new Date(user.createdTime), 7));

  if (!user.id.startsWith(TEMP_USR_ID_PREFIX)) {
    return <></>;
  }

  if (isExpired) {
    return <Chip color="danger">기간 만료</Chip>;
  }

  return <Chip color="warning">미동의 환자</Chip>;
};

export default PatientListContents;
